'use client';

import useScrollReveal from '@/hooks/useScrollReveal';
import AppImage from '@/components/ui/AppImage';


export default function WhySecondChance() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();

  const reasons = [
  {
    id: 1,
    title: 'Talent Was Never the Problem',
    description: 'The issue wasn\'t skill. It was visibility, positioning, and structure. We fix the foundation so talent can finally compete.'
  },
  {
    id: 2,
    title: 'Credibility Opens Doors',
    description: 'Serious brands get serious responses. The right presentation changes who calls back and who signs contracts.'
  },
  {
    id: 3,
    title: 'Chaos Kills Growth',
    description: 'Momentum collapses without systems. We build workflows and infrastructure that protect and multiply effort.'
  },
  {
    id: 4,
    title: 'Structure Beats Sympathy',
    description: 'We\'re not in the business of charity. We build digital foundations that let mission-driven entrepreneurs stand shoulder to shoulder with established companies.'
  }];



  return (
    <section ref={sectionRef} className="relative pb-12 sm:pb-16 md:pb-20 lg:pb-28 overflow-hidden">
      {/* Background Image - Fixed without parallax */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1597058136018-7aaf69ca81f0"
          alt="Industrial warehouse interior with raw concrete walls and gritty urban documentary-style atmosphere"
          width={1920}
          height={1080}
          className="w-full h-full object-cover" />
        {/* Stronger dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/80"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className={`text-center mb-10 sm:mb-12 md:mb-16 pt-12 sm:pt-16 md:pt-20 lg:pt-28 transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 sm:px-6 sm:py-3 bg-brand-green text-white font-body text-xs sm:text-sm font-bold mb-6 sm:mb-8 uppercase tracking-wider">
              OUR MISSION
            </div>
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 sm:mb-8 font-bold leading-tight">
              Why Second-Chance Work Matters
            </h2>
            
            <div className="space-y-4 sm:space-y-6 font-body text-base sm:text-lg md:text-xl text-white leading-relaxed px-4">
              <p className="text-lg sm:text-xl md:text-2xl font-bold">
                The justice-impacted community faces a credibility gap that has nothing to do with skill.
              </p>
              
              <p>
                A talented contractor with a record can't compete with established companies—not because of quality, but because of presentation. A formerly incarcerated entrepreneur can't get taken seriously without professional infrastructure. The work is there. The skill is there. But without the credibility markers that professional branding provides, they're invisible.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm border-l-4 border-brand-teal p-6 sm:p-8">
                <p className="text-2xl sm:text-3xl font-headline font-bold mb-3 sm:mb-4">
                  Branding isn't cosmetic. It's structural.
                </p>
                <p>
                  When we build a professional brand for a second-chance founder, we're not just making things look nice. We're creating the credibility infrastructure that lets talent speak for itself. We're removing the barrier that keeps skilled people from competing on equal footing.
                </p>
              </div>
              
              <p>
                I've watched it work. Founders who couldn't get callbacks now secure commercial contracts. Businesses that were invisible now compete with established companies. Not because their skills improved—they were always talented. Because they finally have the professional presentation that makes people take them seriously.
              </p>
              
              <div className="bg-brand-green p-6 sm:p-8 text-white text-center my-6 sm:my-8">
                <p className="text-2xl sm:text-3xl font-headline mb-2 font-bold">
                  That's not charity.
                </p>
                <p className="text-xl sm:text-2xl font-body font-bold">
                  That's leveling the playing field so talent can actually compete.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {reasons.map((reason, index) => {
            const { ref: cardRef, isVisible: cardVisible } = useScrollReveal<HTMLDivElement>();
            return (
              <div
                key={index}
                ref={cardRef}
                className={`group bg-white p-8 border-l-4 border-brand-teal hover:border-brand-orange transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl shadow-lg ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}>
                
                <h3 className="text-lg font-semibold text-brand-green">{reason.title}</h3>
                <p className="mt-3 text-base text-gray-600">{reason.description}</p>
              </div>);

          })}
        </div>
      </div>
    </section>);

}